<template>
	<div class="results-chart">
		<div v-if="shouldRender" class="results-chart__ie-container">
			<svg viewBox="-1000 -1000 2000 2000" class="results-chart__svg">
				<defs>
					<path
						v-for="textPath in textPaths"
						:id="textPath.id"
						:key="textPath.id"
						:d="textPath.d"
					/>
				</defs>
				<g>
					<ellipse
						v-for="circle in circles"
						:key="circle"
						:rx="circle"
						:ry="circle"
						stroke-width="3"
						stroke="#d0d0d0"
						fill="none"
					/>
					<path
						v-for="(spoke, index) in spokes"
						:key="`spoke-${index}`"
						:d="spoke"
						:stroke="
							index === highlightSection ? '#a0a0a0' : '#d0d0d0'
						"
						stroke-width="14"
						fill="none"
					/>

					<template v-for="name in splitCategoryNames">
						<text
							:key="name.href"
							style="font-size: 55px;"
							text-anchor="middle"
						>
							<textPath
								:xlink:href="`#${name.href}`"
								startOffset="50%"
								text-anchor="middle"
							>
								{{ name.text }}
							</textPath>
						</text>
					</template>
				</g>
				<path
					:d="stroke"
					stroke-width="20"
					:stroke="pathStrokeColor"
					:fill="pathStrokeColor"
					fill-opacity="0.25"
				/>
				<ellipse
					v-for="dot in dots"
					:key="dot.id"
					:cx="dot.x"
					:cy="dot.y"
					rx="10"
					ry="10"
					fill="black"
				/>
				<g>
					<text
						v-if="highlightSection !== -1"
						:x="
							snapCoordinate(
								adjustX(
									getAngle(highlightSection),
									0,
									adjustLength(
										categoryValues[highlightSection]
									)
								)
							)
						"
						:y="
							snapCoordinate(
								adjustY(
									getAngle(highlightSection),
									0,
									adjustLength(
										categoryValues[highlightSection]
									)
								)
							)
						"
						text-anchor="middle"
						dominant-baseline="center"
						style="font-size: 55px;"
					>
						{{ scoreText }}
					</text>
				</g>
				<g>
					<path
						v-for="hoverPath in hover"
						:key="hoverPath.id"
						:d="hoverPath.d"
						stroke-width="0"
						stroke="none"
						fill="transparent"
						@mouseover="onMouseEnter(hoverPath.id)"
						@mouseleave="onMouseLeave(hoverPath.id)"
					/>
				</g>
			</svg>
		</div>
	</div>
</template>

<script>
import survey from "@/survey";
import store from "@/store";
import i18n from "@/plugins/i18n";
import chroma from "chroma-js";

let uniqueId = 0;

export default {
	props: {
		categories: {
			type: Object,
			required: true
		}
	},
	data() {
		return {
			uniqueId: uniqueId++,
			highlightSection: -1
		};
	},
	computed: {
		shouldRender() {
			return Object.keys(this.categories).length > 0;
		},
		locale() {
			return i18n.locale;
		},
		scoreText() {
			if (this.highlightSection === -1) {
				return "";
			}
			return `${Math.round(
				this.categoryValues[this.highlightSection] * 100
			)}%`;
		},
		categoryNames() {
			return Object.keys(this.categories).map(
				id => survey.getCategoryById(id).name[this.locale]
			);
		},
		categoryValues() {
			return Object.values(this.categories);
		},
		splitCategoryNames() {
			const result = [];
			for (let i = 0; i < this.categoryNames.length; i++) {
				const name = this.categoryNames[i];
				const angle = (this.getAngle(i) + Math.PI / 2) % (Math.PI * 2);
				const upperTrackName = angle < Math.PI ? "outer" : "inner";
				const lowerTrackName = angle < Math.PI ? "inner" : "outer";

				const halfLength = name.length / 2;
				const forwardSearch = name.indexOf(" ", halfLength);
				const backwardSearch = name.lastIndexOf(" ", halfLength);
				const splitPoint =
					(halfLength - backwardSearch) >
					(Math.abs(forwardSearch - halfLength))
						? forwardSearch
						: backwardSearch;

				if (splitPoint === -1) {
					result.push({
						href: this.getIdForIndex(`${upperTrackName}-${i}`),
						text: name
					})
				} else {
					result.push(
						{
							href: this.getIdForIndex(`${upperTrackName}-${i}`),
							text: name.substring(0, splitPoint)
						},
						{
							href: this.getIdForIndex(`${lowerTrackName}-${i}`),
							text: name.substring(splitPoint)
						}
					);
				}
			}

			return result;
		},
		categoriesLength() {
			return this.categoryValues.length;
		},
		stroke() {
			const entries = Object.entries(this.categories);
			const stroke = entries.map(([, value], index) =>
				this.getSvgCoordinates(
					this.adjustLength(value),
					this.getAngle(index)
				)
			);
			return `M${stroke.join("L")}Z`;
		},
		spokes() {
			const spokes = [];
			for (let i = 0; i < this.categoriesLength; i++) {
				spokes.push(`
					M${this.getSvgCoordinates(0, this.getAngle(i))}
					L${this.getSvgCoordinates(750, this.getAngle(i))}
				`);
			}
			return spokes;
		},
		circles() {
			const circleSize = this.adjustLength(0.25);
			const maxCircleSize = this.adjustLength(1);
			const circles = [];
			for (let i = circleSize; i <= maxCircleSize; i += circleSize) {
				circles.push(i);
			}
			return circles;
		},
		dots() {
			return this.categoryValues.map((value, i) => ({
				id: i,
				x: this.snapCoordinate(
					this.adjustX(this.getAngle(i), 0, this.adjustLength(value))
				),
				y: this.snapCoordinate(
					this.adjustY(this.getAngle(i), 0, this.adjustLength(value))
				)
			}));
		},
		textPaths() {
			const textPaths = [];
			const angle = this.angleIncrementPerCategory / (Math.PI / 180);
			this.makeTextPath(angle, 900, textPaths, "outer");
			this.makeTextPath(angle, 800, textPaths, "inner");
			return textPaths;
		},
		angleIncrementPerCategory() {
			return (1 / this.categoriesLength) * Math.PI * 2;
		},
		hover() {
			const hover = [];
			const minRadius = 0;
			const maxRadius = 4000;
			for (let i = 0; i < this.categoriesLength; i++) {
				hover.push({
					id: i,
					d: `
					M${this.getSvgCoordinates(minRadius, this.getAngle(i - 0.5))}
					L${this.getSvgCoordinates(maxRadius, this.getAngle(i - 0.5))}
					L${this.getSvgCoordinates(maxRadius, this.getAngle(i + 0.5))}
					L${this.getSvgCoordinates(minRadius, this.getAngle(i + 0.5))}
					Z
				`
				});
			}
			return hover;
		},
		pathStrokeColor() {
			return store.getters.theme.secondaryColor;
		},
		pathFillColor() {
			return chroma(this.pathStrokeColor)
				.alpha(0.25)
				.hex();
		}
	},
	methods: {
		makeTextPath(angle, radius, textPaths, name) {
			for (let i = 0; i < this.categoriesLength; i++) {
				const firstAngle = this.getAngle(i - 0.5);
				let firstX = this.adjustX(firstAngle, 0, radius);
				let firstY = this.adjustY(firstAngle, 0, radius);
				const secondAngle = this.getAngle(i + 0.5);
				let secondX = this.adjustX(secondAngle, 0, radius);
				let secondY = this.adjustY(secondAngle, 0, radius);
				const alternative = firstX < secondX;
				if (alternative) {
					firstX *= 1.05;
					firstY *= 1.05;
					secondX *= 1.05;
					secondY *= 1.05;
				}

				firstX = this.snapCoordinate(firstX);
				firstY = this.snapCoordinate(firstY);
				secondX = this.snapCoordinate(secondX);
				secondY = this.snapCoordinate(secondY);
				textPaths.push({
					id: this.getIdForIndex(name ? `${name}-${i}` : i),
					d: `M${
						alternative
							? `${firstX},${firstY}`
							: `${secondX},${secondY}`
					}
						A${radius},${radius},${angle},0,${alternative ? 0 : 1},
							${alternative ? `${secondX},${secondY}` : `${firstX},${firstY}`}`
				});
			}
		},
		snapCoordinate(coord) {
			return Math.floor(coord);
		},
		adjustLength(value) {
			return (value * 1000) / 1.5;
		},
		getSvgCoordinates(value, angle) {
			const x = this.snapCoordinate(this.adjustX(angle, 0, value));
			const y = this.snapCoordinate(this.adjustY(angle, 0, value));
			return `${x},${y}`;
		},
		getAngle(index) {
			return (index / this.categoriesLength) * Math.PI * 2 + Math.PI;
		},
		adjustX(angle, x, y) {
			const cos = -Math.cos(angle);
			const sin = -Math.sin(angle);
			return cos * x + sin * y;
		},
		adjustY(angle, x, y) {
			const cos = -Math.cos(angle);
			const sin = -Math.sin(angle);
			return -sin * x + cos * y;
		},
		getIdForIndex(index) {
			return `resultschart-textpath-${this.uniqueId}-${index}`;
		},
		onMouseEnter(id) {
			this.highlightSection = id;
		},
		onMouseLeave(id) {
			if (this.highlightSection === id) {
				this.highlightSection = -1;
			}
		}
	}
};
</script>

<style>
.results-chart {
	width: 100%;
	margin: 0 auto;
}

.results-chart__ie-container {
	width: 100%;
	margin: auto;
	text-align: center;
	height: 0;
	padding-bottom: 100%;
	position: relative;
}

@media only screen and (max-width: 767px) {
	.results-chart__ie-container {
		width: 100%;
	}
}

.results-chart__svg {
	position: absolute;
	left: 0;
	top: 0;
	font-family: Verdana, sans-serif;
	font-size: 15px;
}
</style>
